<template>
    <Spinner v-if="loading" />

    <div v-else-if="timeSpan === 'week'" class="calendar-wrapper">
        <div v-for="date in datesOfWeek" :key="date.date" class="date-row">
            <div class="date-row__label">
                <div class="label__week-day">{{ formatDay(date.date) }}</div>
                <span class="label__date" :class="{ today: dateIsToday(date.date) }">{{
                    formatDateDay(date.date)
                }}</span>
            </div>
            <div v-if="date.parties.length > 0" class="date-row__parties">
                <div
                    v-for="party in date.parties"
                    :key="party.id"
                    class="party-entry-party.processStatus.statusDescription"
                    @click="partyClicked(party.id)"
                >
                    <div class="first-row">
                        <div class="host-name" v-if="!party.isDealerParty">{{ hostName(party.hostPersonId) }}</div>
                        <div class="host-name" v-else>{{ $t("widget.calendar.dealerParty") }}</div>
                        <div>{{ formatTime(party.date.date) }}</div>
                    </div>
                    <div class="second-row">
                        <div v-if="!party.isDealerParty">{{ hostAddress(party.hostPersonId) }}</div>
                        <div v-else>{{ $t("widget.calendar.yourAddress") }}</div>
                        <div>#{{ party.id }}</div>
                    </div>
                </div>
            </div>
            <div v-else-if="withSpacer" class="date-row__no-parties">
                <div class="spacer"></div>
            </div>
        </div>
    </div>

    <div v-else-if="timeSpan === 'calendar-parties'" class="calendar-wrapper">
        <div v-for="date in getCalendarParties" :ref="formatScroll(date.date)" :key="date.date" class="date-row">
            <div class="date-row__label">
                <div class="label__week-day">{{ formatDay(date.date) }}</div>
                <span class="label__date" :class="{ today: dateIsSelected(date.date) }">{{
                    formatDateDay(date.date)
                }}</span>
            </div>
            <div v-if="date.parties.length > 0" class="date-row__parties">
                <div
                    v-for="party in date.parties"
                    :key="party.id"
                    class="party-entry"
                    :class="{
                        locked: party.processStatus.statusid == 2,
                        historic: party.processStatus.statusid == 3,
                        discountSent: party.discountCodeStatus == 1,
                        discountUsed: party.discountCodeStatus == 2,
                        partylink: party.isOnline == 1,
                        partylink: party.partylinkClosed == 1,
                    }"
                    @click="partyClicked(party.id)"
                >
                    <div class="first-row">
                        <div class="host-name" v-if="!party.isDealerParty">{{ hostName(party.hostPersonId) }}</div>
                        <div class="host-name" v-else>{{ $t("widget.calendar.dealerParty") }}</div>
                        <div>
                            <div v-if="dealerLocale() == 'fi'">
                                {{
                                    party.partySale.toLocaleString("fi-FI", {
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2,
                                    })
                                }}€
                            </div>
                            <div v-else>
                                {{ party.partySale.toLocaleString("nb-NB", { maximumFractionDigits: 0 }) }}.-
                            </div>
                        </div>
                    </div>
                    <div class="second-row">
                        <div v-if="!party.isDealerParty">{{ hostAddress(party.hostPersonId) }}</div>
                        <div v-else>{{ $t("widget.calendar.yourAddress") }}</div>
                        <div>#{{ party.id }}</div>
                    </div>
                </div>
            </div>
            <div v-else-if="withSpacer" class="date-row__no-parties">
                <div class="spacer"></div>
            </div>
        </div>
    </div>

    <div v-else-if="timeSpan === 'open'" class="calendar-wrapper">
        <div v-for="date in orderDates" :key="date.date" class="date-row">
            <div class="date-row__label">
                <div class="label__week-day">{{ formatDay(date.date) }}</div>
                <span class="label__date" :class="{ today: dateIsToday(date.date) }">{{
                    formatDateDay(date.date)
                }}</span>
            </div>
            <div v-if="date.parties.length > 0" class="date-row__parties">
                <div v-for="party in date.parties" :key="party.id" class="party-entry" @click="partyClicked(party.id)">
                    <div class="first-row">
                        <div v-if="!party.isDealerParty">{{ hostName(party.hostPersonId) }}</div>
                        <div v-else>{{ $t("widget.calendar.dealerParty") }}</div>
                        <div>{{ formatTime(party.date.date) }}</div>
                    </div>
                    <div class="second-row">
                        <div v-if="!party.isDealerParty">{{ hostAddress(party.hostPersonId) }}</div>
                        <div v-else>{{ $t("widget.calendar.yourAddress") }}</div>
                        <div>#{{ party.id }}</div>
                    </div>
                </div>
            </div>
            <div v-else-if="withSpacer" class="date-row__no-parties">
                <div class="spacer"></div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from "moment";
    import { computed, ref } from "vue";
    import { useStore, mapGetters } from "vuex";
    import Spinner from "@/components/core/Spinner.vue";
    export default {
        name: "PartyCalendar",
        components: { Spinner },
        emits: ["party-clicked"],
        props: {
            withSpacer: {
                type: Boolean,
                default: true,
            },
            timeSpan: {
                type: String,
                default: "week",
            },
            selectedDate: {
                default: moment(),
            },
        },
        watch: {},
        computed: {
            ...mapGetters(["getCalendarParties", "dealerLocale"]),
        },
        methods: {
            partyClicked(id) {
                this.$emit("party-clicked", id);
            },
            formatDay(day) {
                return moment(day).format("dddd").substr(0, 3).toUpperCase();
            },
            isFirstOfMonth(day) {
                return moment(day).date() === 1;
            },

            monthFormat(day) {
                return moment(day).format("MMMM YYYY");
            },
            dateIsToday(day) {
                let currentDate = moment();
                return currentDate.startOf("day").isSame(moment(day).startOf("day"));
            },
            dateIsSelected(day) {
                return this.selectedDate.startOf("day").isSame(moment(day).startOf("day"));
            },
            formatDateDay(day) {
                return moment(day).format("D");
            },
            formatTime(day) {
                return moment(day).format("HH:mm");
            },
            formatScroll(day) {
                return moment(day).format("YYYY-MM-DD");
            },
            hostName(personId) {
                let cust = this.$store.getters.getCustomer(personId);
                if (cust) {
                    return cust.firstName + " " + cust.lastName;
                } else {
                    return "Name missing from contacts";
                }
            },
            hostAddress(personId) {
                let cust = this.$store.getters.getCustomer(personId);
                if (cust && cust.primaryAddress) {
                    let addr = cust.primaryAddress;
                    let addressParts = [addr.addressLine1, addr.postCode, addr.postLocation];
                    return addressParts.filter((part) => part).join(", ");
                } else {
                    return "";
                }
            },
        },
        setup(props) {
            const store = useStore();
            const loading = ref(true);

            store
                .dispatch("loadAllCustomers")
                .then(() => {
                    if (props.timeSpan === "week") {
                        store.dispatch("getCalendar").then(() => {
                            loading.value = false;
                        });
                    } else if (props.timeSpan === "open") {
                        store.dispatch("loadOpenOrder").then(() => {
                            loading.value = false;
                        });
                    } else if (props.timeSpan === "calendar-parties") {
                        loading.value = false;
                    }
                })
                .catch(() => {
                    loading.value = false;
                });

            return {
                datesOfWeek: computed(() => store.getters.parties),
                orderDates: computed(() => store.getters.orderParties),
                loading,
            };
        },
    };
</script>

<style scoped></style>
